import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  Text,
  Grid,
  Card,
  Badge,
  Button,
  TextInput,
} from '@tremor/react';
import { ShieldCheck, KeyRound, AlertTriangle, Activity } from 'lucide-react';
import { KPICard } from '@/components/KPICard';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage, buildQueryString } from '@/lib/api';
import {
  formatRangeShort,
  useDateRange,
  useDateRangeKey,
} from '@/lib/dateRange';

interface AuditEntry {
  id: number;
  admin_email: string;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  details: unknown;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string | null;
}

interface AuditResponse {
  items: AuditEntry[];
  total: number;
}

interface AuditStats {
  failed_attempts: number;
  by_action: Array<{ action: string; count: number }>;
  by_admin: Array<{ admin_email: string; count: number; last_action: string | null }>;
  by_day: Array<{ day: string; count: number }>;
}

interface SessionsResponse {
  items: Array<{
    admin_email: string;
    active_sessions: number;
    last_issued: string | null;
    last_login_at: string | null;
  }>;
}

const fmtInt = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : new Intl.NumberFormat('pt-BR').format(n);

export function SecurityPage() {
  const qc = useQueryClient();
  const { range, apiParams } = useDateRange();
  const rangeKey = useDateRangeKey();
  const [actionFilter, setActionFilter] = useState('');

  const stats = useQuery<AuditStats>({
    queryKey: ['security.stats', rangeKey],
    queryFn: async () =>
      (await api().get('/security/audit/stats', { params: apiParams })).data,
  });

  const audit = useQuery<AuditResponse>({
    queryKey: ['security.audit', actionFilter, rangeKey],
    queryFn: async () => {
      const qs = buildQueryString({
        ...apiParams,
        per_page: 100,
        action: actionFilter || undefined,
      });
      return (await api().get(`/security/audit${qs}`)).data;
    },
  });

  const sessions = useQuery<SessionsResponse>({
    queryKey: ['security.sessions'],
    queryFn: async () => (await api().get('/security/sessions')).data,
    refetchInterval: 30_000,
  });

  // Change password form
  const [pwd, setPwd] = useState({ current_password: '', new_password: '', totp_code: '' });
  const [pwdMsg, setPwdMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const changePwdMutation = useMutation({
    mutationFn: async () => {
      const r = await api().post('/auth/change-password', pwd);
      return r.data;
    },
    onSuccess: () => {
      setPwdMsg({ type: 'ok', text: 'Senha trocada — você precisará fazer login de novo.' });
      setPwd({ current_password: '', new_password: '', totp_code: '' });
      qc.invalidateQueries({ queryKey: ['security'] });
    },
    onError: (err) => {
      setPwdMsg({
        type: 'err',
        text: extractErrorMessage(err, 'erro ao trocar senha'),
      });
    },
  });

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Title>Security</Title>
        <Text>Audit log, sessões ativas, troca de senha.</Text>
      </header>

      <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
        <KPICard
          label={`Eventos · ${formatRangeShort(range)}`}
          value={stats.isLoading ? null : fmtInt(audit.data?.total)}
          loading={stats.isLoading || audit.isLoading}
        />
        <KPICard
          label="Tentativas negadas"
          value={stats.isLoading ? null : fmtInt(stats.data?.failed_attempts)}
          loading={stats.isLoading}
          error={stats.data && stats.data.failed_attempts > 5 ? 'Muitas falhas' : undefined}
        />
        <KPICard
          label="Admins ativos"
          value={stats.isLoading ? null : fmtInt(sessions.data?.items.length)}
          loading={sessions.isLoading}
          hint="Sessões válidas agora"
        />
        <KPICard
          label="Ações distintas"
          value={stats.isLoading ? null : fmtInt(stats.data?.by_action.length)}
          loading={stats.isLoading}
        />
      </Grid>

      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-amber-300" />
            <Title className="text-base">Trocar senha</Title>
          </div>
          <div className="mt-3 space-y-2">
            <TextInput
              type="password"
              placeholder="Senha atual"
              value={pwd.current_password}
              onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
            />
            <TextInput
              type="password"
              placeholder="Nova senha (mínimo 8 chars)"
              value={pwd.new_password}
              onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
            />
            <TextInput
              placeholder="Código TOTP (6 dígitos)"
              maxLength={6}
              value={pwd.totp_code}
              onChange={(e) => setPwd({ ...pwd, totp_code: e.target.value })}
            />
            <Button
              onClick={() => changePwdMutation.mutate()}
              disabled={
                changePwdMutation.isPending ||
                !pwd.current_password ||
                pwd.new_password.length < 8 ||
                pwd.totp_code.length !== 6
              }
              loading={changePwdMutation.isPending}
            >
              Trocar senha
            </Button>
            {pwdMsg && (
              <div
                className={`rounded p-2 text-sm ${pwdMsg.type === 'ok' ? 'bg-emerald-900/30 text-emerald-300' : 'bg-red-900/30 text-red-300'}`}
              >
                {pwdMsg.text}
              </div>
            )}
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-blue-300" />
            <Title className="text-base">Sessões ativas</Title>
          </div>
          {sessions.isLoading && <LoadingSkeleton rows={3} />}
          {!sessions.isLoading && (
            <div className="mt-3 space-y-1">
              {sessions.data?.items.length === 0 && (
                <Text className="text-slate-400">Nenhuma sessão ativa.</Text>
              )}
              {sessions.data?.items.map((s) => (
                <div key={s.admin_email} className="flex items-center justify-between border-b border-slate-800/50 py-1.5 text-sm">
                  <span className="text-slate-200">{s.admin_email}</span>
                  <div className="flex items-center gap-2">
                    <Badge color="blue" size="xs">
                      {s.active_sessions} sessão(ões)
                    </Badge>
                    <span className="text-xs text-slate-500">
                      {s.last_login_at && new Date(s.last_login_at).toLocaleString('pt-BR')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </Grid>

      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-300" />
            <Title className="text-base">Audit log · {formatRangeShort(range)}</Title>
            {audit.data && (
              <Text className="text-xs text-slate-400">
                {fmtInt(audit.data.total)} eventos
              </Text>
            )}
          </div>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="w-64 rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm"
          >
            <option value="">Todas as ações</option>
            {stats.data?.by_action.map((a) => (
              <option key={a.action} value={a.action}>
                {a.action} ({a.count})
              </option>
            ))}
          </select>
        </div>
        {audit.isLoading && <LoadingSkeleton rows={5} />}
        {!audit.isLoading && (
          <div className="mt-3 max-h-[600px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-950">
                <tr className="border-b border-slate-800 text-left text-xs text-slate-400">
                  <th className="py-2">Quando</th>
                  <th>Quem</th>
                  <th>Ação</th>
                  <th>Recurso</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {audit.data?.items.map((e) => {
                  const isDenied = e.action.includes('denied');
                  return (
                    <tr key={e.id} className="border-b border-slate-800/30 text-xs">
                      <td className="py-1.5 text-slate-400">
                        {e.created_at && new Date(e.created_at).toLocaleString('pt-BR')}
                      </td>
                      <td className="text-slate-300">{e.admin_email}</td>
                      <td>
                        <Badge
                          color={isDenied ? 'red' : e.action.includes('success') ? 'emerald' : 'slate'}
                          size="xs"
                          icon={isDenied ? AlertTriangle : undefined}
                        >
                          {e.action}
                        </Badge>
                      </td>
                      <td className="text-slate-400">
                        {e.resource_type ? `${e.resource_type}/${e.resource_id ?? ''}` : '—'}
                      </td>
                      <td className="font-mono text-slate-500">{e.ip_address ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
